<template>
  <FlagProvider :unleash-client="unleashClient">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </FlagProvider>
</template>

<script setup lang="ts">
import { FlagProvider, UnleashClient } from '@unleash/proxy-client-vue';

import { useBaseSentry } from '@/composables/utils/useSentry';
import { stateFactoryKey, useFetchFromPublicWabunkaApiKey, useFetchKey, useMicroCMSGetListKey, useRouteKey, useRouterKey, useRuntimeConfigKey, useSentryKey, useStateKey } from '@/injectionKeys';
import { useMicroCMSGetList } from '@/wabunka-src/common/composables/query/useMicroCMSClient';

provide(useRuntimeConfigKey, useRuntimeConfig);
provide(useFetchFromPublicWabunkaApiKey, useFetchFromPublicWabunkaApi);
provide(useRouterKey, useRouter);
provide(useRouteKey, useRoute);
provide(stateFactoryKey, stateFactory);
provide(useStateKey, useState);
provide(useSentryKey, useBaseSentry);
provide(useMicroCMSGetListKey, useMicroCMSGetList);
provide(useFetchKey, useFetch);

const runtimeConfig = useRuntimeConfig();

// unleashの設定
// NOTE: フィーチャーフラグを導入する場合はこちらのドキュメントを参照：https://www.notion.so/jcat/Unleash-60baf1aa42ff4da98f22049b36856165?pvs=4
const unleashConfig = {
  url: runtimeConfig.public.UNLEASH_API_URL,
  clientKey: runtimeConfig.public.UNLEASH_API_TOKEN,
  refreshInterval: 15,
  appName: runtimeConfig.public.UNLEASH_API_APP_NAME,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let unleashClient: any;
try {
  unleashClient = new UnleashClient(unleashConfig);
  // start() は refresh と metrics の setInterval を張るので、サーバーで呼ぶと SSR・prerender のプロセスが終わらない
  if (import.meta.client) {
    unleashClient.start();
  }
} catch (e) {
  console.error(e);
}

const commonMeta = [
  { charset: 'utf-8' },
  { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1.0' },
  {
    name: 'google-site-verification',
    content: 'XeqBkILvFEvd_Dn2oQaA1QPz7yK9KqYS1WUJCAO27G0',
  },
  {
    name: 'referrer',
    content: 'no-referrer-when-downgrade',
  },
];

const link = [
  { rel: 'icon' as const, type: 'image/x-icon', href: '/favicon.ico' },
  { rel: 'icon' as const, sizes: '192x192', href: '/android-touch-icon.png' },
  { rel: 'apple-touch-icon' as const, sizes: '180x180', href: '/apple-touch-icon.png' },
];

// ref: https://www.notion.so/jcat/GTM-GA4-5bf8e7f04e8b4509bc324fd4a5fde096?pvs=4
const gtmScriptUrl = runtimeConfig.public.GTM_GATEWAY_PATH ? `/${runtimeConfig.public.GTM_GATEWAY_PATH}/gtm.js?id=` : 'https://www.googletagmanager.com/gtm.js?id=';
const gtmScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= '${gtmScriptUrl}'+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','${runtimeConfig.public.GOOGLE_TAG_MANAGER_CONTAINER_ID}');`;

// Optimize Next ローダー（A/B テスト配信。window.optimize_next を生やす）
// アンチフリッカーは不採用（KV は SSR 即時表示を維持）。判定は各セクションのハイドレーション後に行う。
// NOTE: 実験 OFF（experience ID が空）でも常時ロードする。experiment ID で条件ロードにすると、
//       GUI で実験を起動するたびに env 変更＋再デプロイが必要になり、Optimize Next を GUI 完結で回す
//       利点が失われるため、意図的に常時注入する。
//
// Optimize Next の GTM コンテナ ID は env 別（dev/staging/local と本番で別コンテナ）。
// runtimeConfig.public.OPTIMIZE_NEXT_CONTAINER_ID（env/.env.* で注入）を参照する。
// 空文字（未設定）の場合はローダーを注入しない（`GTM-` の不正 ID ロードを避ける）。
const optimizeNextContainerId = (runtimeConfig.public.OPTIMIZE_NEXT_CONTAINER_ID ?? '') as string;
const optimizeNextScript = optimizeNextContainerId
  ? `(function(o,p,n,e,x){if(o['_'+n])return;o['_'+n]={};e=p.currentScript;x=p.createElement('script');x.src='https://www.googletagmanager.com/gtm.js?id=GTM-'+n+(location.search.includes('_optx_preview')?'&t='+new Date().getTime():'');e.parentNode.insertBefore(x,e)})(window,document,'${optimizeNextContainerId}')`
  : '';

// Rakuten Advertising Global Tag
const rakutenScript = `(function (url) {
    /*Tracking Bootstrap Set Up DataLayer objects/properties here*/
    window.rakutenDataLayer = false; // Set custom DataLayer name if 'DataLayer' conflicts
    var dln = window.rakutenDataLayer || 'DataLayer';
    if(!window[dln]){
      window[dln] = {};
    }
    if(!window[dln].events){
      window[dln].events = {};
    }
    window[dln].events.SPIVersion = window[dln].events.SPIVersion || "v3.4.1";

    var loc, ct = document.createElement("script");
    ct.type = "text/javascript";
    ct.async = true; ct.src = url; loc = document.getElementsByTagName('script')[0];
    loc.parentNode.insertBefore(ct, loc);
    }(document.location.protocol + "//tag.rmp.rakuten.com/127107.ct.js"));`;

const organizationJsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Wabunka',
  url: 'https://wabunka-lux.jp',
  logo: 'https://wabunka-lux.jp/android-touch-icon.png',
  sameAs: ['https://www.instagram.com/wabunka.japan/', 'https://www.facebook.com/wabunka.japan/', 'https://www.youtube.com/@WabunkaJapan'],
});

useHead({
  meta: commonMeta,
  link,
  script: [gtmScript, ...(optimizeNextScript ? [optimizeNextScript] : []), rakutenScript, { type: 'application/ld+json', innerHTML: organizationJsonLd }],
});
</script>
